import { useState } from "react";
import { PageLayout } from "@/components/layout/PageLayout";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useWallet } from "@/contexts/WalletContext";
import { toast } from "@/hooks/use-toast";
import {
  Store, Search, Star, Crown, Sparkles, Trophy,
  Shield, Zap, Palette, ShoppingCart, Loader2
} from "lucide-react";
import { cn } from "@/lib/utils";

interface MarketplaceItem {
  id: string;
  name: string;
  description: string;
  price: number;
  category: "badge" | "frame" | "effect" | "boost" | "cosmetic";
  rarity: "common" | "rare" | "epic" | "legendary";
  icon: typeof Star;
  available: number;
  sold: number;
}

const MARKETPLACE_ITEMS: MarketplaceItem[] = [
  {
    id: "badge-diamond",
    name: "Diamond Hands Badge",
    description: "Show everyone you hold through anything",
    price: 0.5,
    category: "badge",
    rarity: "epic",
    icon: Trophy,
    available: 1000,
    sold: 234,
  },
  {
    id: "frame-golden",
    name: "Golden Profile Frame",
    description: "A prestigious golden frame for your profile",
    price: 1.0,
    category: "frame",
    rarity: "legendary",
    icon: Crown,
    available: 500,
    sold: 89,
  },
  {
    id: "effect-sparkle",
    name: "Sparkle Effect",
    description: "Add sparkles to your trades and messages",
    price: 0.25,
    category: "effect",
    rarity: "rare",
    icon: Sparkles,
    available: 5000,
    sold: 1203,
  },
  {
    id: "boost-priority",
    name: "Priority Queue Pass",
    description: "7 days of priority trade execution",
    price: 0.1,
    category: "boost",
    rarity: "common",
    icon: Zap,
    available: 10000,
    sold: 4521,
  },
  {
    id: "badge-whale",
    name: "Whale Badge",
    description: "Exclusive badge for big traders",
    price: 5.0,
    category: "badge",
    rarity: "legendary",
    icon: Star,
    available: 100,
    sold: 23,
  },
  {
    id: "cosmetic-avatar",
    name: "Custom Avatar Frame",
    description: "Animated avatar border",
    price: 0.75,
    category: "cosmetic",
    rarity: "epic",
    icon: Palette,
    available: 2000,
    sold: 567,
  },
  {
    id: "badge-verified",
    name: "Verified Trader Badge",
    description: "Blue checkmark for your profile",
    price: 2.5,
    category: "badge",
    rarity: "legendary",
    icon: Shield,
    available: 200,
    sold: 45,
  },
  {
    id: "boost-mev",
    name: "MEV Protection Pass",
    description: "30 days of MEV protection on trades",
    price: 0.5,
    category: "boost",
    rarity: "rare",
    icon: Shield,
    available: 3000,
    sold: 892,
  },
];

const RARITY_COLORS = {
  common: "text-muted-foreground border-border",
  rare: "text-blue-400 border-blue-500/50",
  epic: "text-purple-400 border-purple-500/50",
  legendary: "text-yellow-400 border-yellow-500/50",
};

const RARITY_BG = {
  common: "bg-secondary",
  rare: "bg-blue-500/20",
  epic: "bg-purple-500/20",
  legendary: "bg-yellow-500/20",
};

export default function Marketplace() {
  const { connected, connect, balance } = useWallet();
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [cart, setCart] = useState<string[]>([]);
  const [isPurchasing, setIsPurchasing] = useState(false);

  const filteredItems = MARKETPLACE_ITEMS.filter((item) => {
    const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = selectedCategory === "all" || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const addToCart = (itemId: string) => {
    if (cart.includes(itemId)) {
      toast({ title: "Already in cart" });
      return;
    }
    setCart([...cart, itemId]);
    toast({ title: "Added to cart" });
  };

  const removeFromCart = (itemId: string) => {
    setCart(cart.filter(id => id !== itemId));
  };

  const cartItems = MARKETPLACE_ITEMS.filter(item => cart.includes(item.id));
  const cartTotal = cartItems.reduce((sum, item) => sum + item.price, 0);

  const handlePurchase = async () => {
    if (!connected) {
      connect();
      return;
    }

    if (balance < cartTotal) {
      toast({ title: "Insufficient balance", variant: "destructive" });
      return;
    }

    setIsPurchasing(true);
    
    // Simulate purchase
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    toast({ 
      title: "Purchase Successful!", 
      description: `You purchased ${cartItems.length} item(s) for ${cartTotal} SOL` 
    });
    
    setCart([]);
    setIsPurchasing(false);
  };

  return (
    <>
      <Helmet>
        <title>Marketplace - TheMoonRun</title>
        <meta name="description" content="Buy and sell digital assets, badges, and cosmetics on TheMoonRun marketplace." />
      </Helmet>

      <PageLayout>
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
              <Store className="w-7 h-7 text-purple-500" />
            </div>
            <div>
              <h1 className="text-2xl font-display font-bold text-foreground">Marketplace</h1>
              <p className="text-muted-foreground">Collect badges, cosmetics & boosts</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {connected && (
              <Badge variant="secondary" className="font-mono">
                {balance.toFixed(2)} SOL
              </Badge>
            )}
            <Button variant="outline" className="relative">
              <ShoppingCart className="w-4 h-4" />
              Cart ({cart.length})
              {cart.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                  {cart.length}
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search items..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {["all", "badge", "frame", "effect", "boost", "cosmetic"].map((cat) => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(cat)}
                className="capitalize"
              >
                {cat}
              </Button>
            ))}
          </div>
        </div>

        {/* Cart Summary (if items in cart) */}
        {cart.length > 0 && (
          <div className="p-4 rounded-xl bg-card border border-primary/30 mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-foreground mb-1">Shopping Cart</h3>
                <p className="text-sm text-muted-foreground">
                  {cartItems.map(i => i.name).join(", ")}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono font-bold text-primary">{cartTotal.toFixed(2)} SOL</span>
                <Button 
                  variant="hero" 
                  onClick={handlePurchase}
                  disabled={isPurchasing}
                >
                  {isPurchasing ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Purchasing...</>
                  ) : (
                    "Purchase Now"
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Featured Banner */}
        <div className="p-4 sm:p-6 rounded-2xl bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-orange-500/20 border border-purple-500/30 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <Badge className="mb-2 bg-purple-500">Featured</Badge>
              <h2 className="text-lg sm:text-xl font-bold text-foreground mb-1">Season 1 Collection</h2>
              <p className="text-sm text-muted-foreground">Limited edition items available now</p>
            </div>
            <Button variant="hero">
              <Sparkles className="w-4 h-4" />
              View Collection
            </Button>
          </div>
        </div>

        {/* Items Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredItems.map((item) => {
            const inCart = cart.includes(item.id);
            const soldOut = item.sold >= item.available;
            
            return (
              <div
                key={item.id}
                className={cn(
                  "p-4 sm:p-5 rounded-xl bg-card border-2 transition-all",
                  inCart ? "border-primary" : RARITY_COLORS[item.rarity],
                  !soldOut && "hover:scale-[1.02]"
                )}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={cn(
                    "w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center",
                    RARITY_BG[item.rarity]
                  )}>
                    <item.icon className={cn(
                      "w-5 h-5 sm:w-6 sm:h-6",
                      item.rarity === "legendary" && "text-yellow-400",
                      item.rarity === "epic" && "text-purple-400",
                      item.rarity === "rare" && "text-blue-400",
                      item.rarity === "common" && "text-muted-foreground"
                    )} />
                  </div>
                  <Badge variant="outline" className="capitalize text-xs">
                    {item.rarity}
                  </Badge>
                </div>

                <h3 className="font-semibold text-foreground mb-1">{item.name}</h3>
                <p className="text-sm text-muted-foreground mb-4">{item.description}</p>

                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Price</p>
                    <p className="font-mono font-bold text-primary">{item.price} SOL</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Available</p>
                    <p className="font-mono text-foreground">{item.available - item.sold}</p>
                  </div>
                </div>

                <div className="w-full h-1.5 rounded-full bg-secondary mb-4">
                  <div
                    className={cn(
                      "h-full rounded-full",
                      soldOut ? "bg-destructive" : "bg-primary"
                    )}
                    style={{ width: `${Math.min(100, (item.sold / item.available) * 100)}%` }}
                  />
                </div>

                {soldOut ? (
                  <Button variant="outline" className="w-full" disabled>
                    Sold Out
                  </Button>
                ) : inCart ? (
                  <Button 
                    variant="destructive" 
                    className="w-full"
                    onClick={() => removeFromCart(item.id)}
                  >
                    Remove from Cart
                  </Button>
                ) : (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => addToCart(item.id)}
                  >
                    <ShoppingCart className="w-4 h-4" />
                    Add to Cart
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {filteredItems.length === 0 && (
          <div className="text-center py-12">
            <Store className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">No items found</p>
          </div>
        )}
      </PageLayout>
    </>
  );
}
